import React, { useState, useEffect } from 'react';
import { api, logHttpError } from '../services/api';
import ModerationPanel from '../components/admin/ModerationPanel';
import EventsPanel from '../components/admin/EventsPanel';

interface AdminUser {
  id: number;
  username: string;
  full_name: string;
  email: string;
  clan_id: number;
  mood_score: number;
  avatar_url: string | null;
  is_admin: boolean;
}

const CLAN_NAMES: Record<number, string> = {
  1: 'Акула',
  2: 'Слон',
  3: 'Горилла',
  4: 'Волк'
};

const AdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'users' | 'moderation' | 'events'>('users');
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<number | null>(null);
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await api.get('/auth/admin/users');
      setUsers(res.data);
    } catch (error: unknown) {
      logHttpError("Ошибка загрузки пользователей", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleToggleBan = async (targetUserId: number) => {
    try {
      setActionLoading(targetUserId);
      await api.post(`/auth/admin/users/${targetUserId}/toggle-ban`, {});
      await fetchUsers();
    } catch (err) {
      alert("Не удалось изменить статус");
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleAdmin = async (targetUserId: number) => {
    try {
      setActionLoading(targetUserId);
      const res = await api.post(`/auth/admin/users/${targetUserId}/toggle-admin`, {});
      await fetchUsers();
      alert(res.data.is_admin ? "Пользователь стал админом!" : "Пользователь больше не админ");
    } catch (error: unknown) {
      logHttpError("Ошибка изменения прав администратора", error);
      alert("Не удалось изменить права администратора.");
    } finally {
      setActionLoading(null);
    }
  };

  const handleGiveAward = async (targetUserId: number) => {
    const awardName = prompt("Введите название награды:", "Мастер дискуссий");
    if (!awardName) return;

    try {
      setActionLoading(targetUserId);
      await api.post(
        `/auth/admin/users/${targetUserId}/award`,
        { achievement_name: awardName }
      );
      alert(`Награда "${awardName}" выдана!`);
    } catch (err) {
      alert("Ошибка выдачи награды");
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6 pb-24 md:pb-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <div>
          <h1 className="text-3xl font-bold text-red-400 flex items-center gap-3">🛡️ Панель Администратора</h1>
          <p className="text-gray-400 mt-1">Управление пользователями, правами, наградами и контентом</p>
        </div>
        {activeTab === 'users' && (
          <button
            onClick={fetchUsers}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-white text-sm font-bold transition border border-gray-700"
          >
            🔄 Обновить список
          </button>
        )}
      </div>

      <div className="mb-6 flex gap-2 border-b border-gray-800" role="tablist" aria-label="Раздел администратора">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'users'}
          onClick={() => setActiveTab('users')}
          className={`border-b-2 px-4 py-3 text-sm font-bold transition ${
            activeTab === 'users'
              ? 'border-red-400 text-red-300'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Пользователи
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'moderation'}
          onClick={() => setActiveTab('moderation')}
          className={`border-b-2 px-4 py-3 text-sm font-bold transition ${
            activeTab === 'moderation'
              ? 'border-red-400 text-red-300'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          Модерация
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === 'events'}
          onClick={() => setActiveTab('events')}
          className={`border-b-2 px-4 py-3 text-sm font-bold transition ${
            activeTab === 'events'
              ? 'border-red-400 text-red-300'
              : 'border-transparent text-gray-400 hover:text-white'
          }`}
        >
          События
        </button>
      </div>

      {activeTab === 'events' ? (
        <EventsPanel />
      ) : activeTab === 'moderation' ? (
        <ModerationPanel />
      ) : loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin w-10 h-10 border-4 border-red-500 border-t-transparent rounded-full"></div>
        </div>
      ) : users.length === 0 ? (
        <div className="text-center py-20 bg-[#1a1d24] rounded-2xl border border-gray-800">
          <p className="text-gray-400">Пользователи не найдены</p>
        </div>
      ) : (
        <div className="bg-[#1a1d24] rounded-2xl border border-gray-800 overflow-hidden shadow-xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="bg-gray-900/50 border-b border-gray-800">
                <tr>
                  <th className="p-4 text-gray-400 font-medium text-sm uppercase tracking-wider">ID</th>
                  <th className="p-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Никнейм</th>
                  <th className="p-4 text-gray-400 font-medium text-sm uppercase tracking-wider">ФИО</th>
                  <th className="p-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Клан</th>
                  <th className="p-4 text-gray-400 font-medium text-sm uppercase tracking-wider">Статус</th>
                  <th className="p-4 text-gray-400 font-medium text-sm uppercase tracking-wider text-right">Действия</th>
                </tr>
              </thead>
              <tbody>
                {users.map(user => (
                  <tr key={user.id} className="border-b border-gray-800/50 hover:bg-gray-800/30 transition">
                    <td className="p-4 text-gray-500 font-mono text-sm">#{user.id}</td>
                    <td className="p-4 font-bold text-white flex items-center gap-2">
                      {user.username}
                      {user.is_admin && (
                        <span className="bg-red-500/20 text-red-400 text-[10px] px-2 py-0.5 rounded-full border border-red-500/30 font-bold uppercase">
                          👑 АДМИН
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-gray-300 text-sm">{user.full_name}</td>
                    <td className="p-4">
                      <span className="px-2 py-1 rounded text-xs font-bold bg-blue-500/10 text-blue-400 border border-blue-500/20">
                        {CLAN_NAMES[user.clan_id] || 'Неизвестно'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex flex-col gap-1">
                        <span className={`px-3 py-1 rounded-full text-xs font-bold uppercase ${
                          user.mood_score < 0
                            ? 'bg-red-500/20 text-red-400 border border-red-500/30' 
                            : 'bg-green-500/20 text-green-400 border border-green-500/30'
                        }`}>
                          {user.mood_score < 0 ? '🚫 Забанен' : '✅ Активен'}
                        </span>
                      </div>
                    </td>
                    <td className="p-4 text-right space-x-2">
                      <button
                        onClick={() => handleGiveAward(user.id)}
                        disabled={actionLoading === user.id}
                        className="px-3 py-1.5 bg-yellow-500/20 hover:bg-yellow-500/30 disabled:opacity-50 text-yellow-400 rounded-lg text-sm font-bold transition border border-yellow-500/30"
                      >
                        🏆 Награда
                      </button>
                      <button
                        onClick={() => handleToggleAdmin(user.id)}
                        disabled={actionLoading === user.id}
                        className={`px-3 py-1.5 rounded-lg text-sm font-bold transition border disabled:opacity-50 ${
                          user.is_admin 
                            ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-400 border-purple-500/30' 
                            : 'bg-gray-700 hover:bg-gray-600 text-gray-300 border-gray-600'
                        }`}
                      >
                        {user.is_admin ? '👑 Админ' : '🛡️ Сделать админом'}
                      </button>
                      <button
                        onClick={() => handleToggleBan(user.id)}
                        disabled={actionLoading === user.id}
                        className={`px-3 py-1.5 rounded-lg text-sm font-bold transition border disabled:opacity-50 ${
                          user.mood_score < 0
                            ? 'bg-green-500/20 hover:bg-green-500/30 text-green-400 border-green-500/30' 
                            : 'bg-red-500/20 hover:bg-red-500/30 text-red-400 border-red-500/30'
                        }`}
                      >
                        {user.mood_score < 0 ? '✅ Разбан' : '🚫 Бан'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPage;
